import { Settings } from '../../../domain';
import { logError, logWarning } from '../../../utils/logging';
import { GeneralTranslations } from '../types';
import { getTranslationsMetaobject } from './getTranslationsMetaobject';
import { TRANSLATION_CONFIG } from './translationConfig';

const TRANSLATIONS_KEY = 'gift_box_custom_wording';

/**
 * Build translations object with fallback support
 */
export const buildTranslations = (
  translationsMetaobjectData: Record<string, string> | null,
  settings: Settings,
): GeneralTranslations => {
  const translations = {} as GeneralTranslations;

  for (const [translationKey, config] of Object.entries(TRANSLATION_CONFIG)) {
    const key = translationKey as keyof GeneralTranslations;
    const settingsKey = config.settingsKey || translationKey;

    // Priority 1: Metaobject translations
    if (translationsMetaobjectData?.[key]) {
      translations[key] = translationsMetaobjectData[key];
      continue;
    }

    // Priority 2: Settings fallback
    if (settings[settingsKey as keyof Settings]) {
      const value = settings[settingsKey as keyof Settings];
      if (value && typeof value === 'string') {
        logWarning(
          `Translation for ${key} not found in metaobject, using settings fallback (${settingsKey})`,
        );
        translations[key] = value;
        continue;
      }
    }

    // Priority 3: Hard-coded fallback
    if (config.fallback) {
      logWarning(`Translation for ${key} not found, using hard-coded fallback`);
      translations[key] = config.fallback;
      continue;
    }

    // Default: empty string
    logWarning(`Translation for ${key} not found and no fallback provided`);
    translations[key] = '';
  }

  return translations;
};

export const getGeneralMetaobject = async (storefrontAccessToken: string) => {
  if (!storefrontAccessToken) {
    return null;
  }

  let translationsMetaobjectData: Record<string, string> | null = null;

  try {
    translationsMetaobjectData = await getTranslationsMetaobject(
      storefrontAccessToken,
      TRANSLATIONS_KEY,
    );
  } catch (error) {
    // Graceful degradation: continue with fallbacks if metaobject fetch fails
    logError(`Failed to fetch translations metaobject: ${error}`);
  }

  return translationsMetaobjectData;
};

/**
 * Fetch and build translations with fallback support
 */
export const getTranslations = async (
  storefrontAccessToken: string,
  settings: Settings,
): Promise<GeneralTranslations> => {
  if (!storefrontAccessToken) {
    return buildTranslations(null, settings);
  }

  let translationsMetaobjectData: Record<string, string> | null = null;

  try {
    translationsMetaobjectData = await getTranslationsMetaobject(
      storefrontAccessToken,
      TRANSLATIONS_KEY,
    );
  } catch (error) {
    // Graceful degradation: continue with fallbacks if metaobject fetch fails
    logError(`Failed to fetch translations metaobject: ${error}`);
  }

  return buildTranslations(translationsMetaobjectData, settings);
};
